/**
 * The outline somebody is drawing on `/umriss`, kept in `localStorage` until
 * „Übernehmen“ — so a reload, a closed tab or a crashed browser does not mean
 * drawing the house again from the rectangle up (Welle 1, idea 12).
 *
 * In `lib/` and not in the store: `store/outlineDraft.ts` holds the drawing
 * and its steps for the tab, this module only reads and writes a copy of the
 * shape as it stands. The screen decides when (`OutlineDraftScreen`).
 *
 * **One place per way in**, keyed by the way and the Vorgang: a new outline
 * by hand, Stand 1 of a given Vorgang, „Form ändern …“ of a Vorgang, the
 * editor seeded from a reading. A drawing for one never turns up in another.
 * The Stand or reading a seeded drawing was taken from is *checked*, not
 * keyed (`basis`): a drawing made on Stand 3 is not offered once the Vorgang
 * is at Stand 4 — the screen starts from Stand 4 and the old copy goes,
 * exactly as the tab's own drawing does (`ensureDraftFor`) — instead of
 * lying around under a key nobody opens again.
 *
 * **What is stored is the shape and nothing else** — corners, walls, the
 * figures somebody typed and the labels. No title, no customer, no answer of
 * the server, no undo steps. Two marks a wall may carry ride along because
 * without them a restored drawing would hand back a different claim than the
 * one drawn: `fromWallId` (which wall of the Stand it continues — without it
 * every figure of „Form ändern …“ would come back as the person's own, and
 * every correction would go missing from the trail) and `readMm` (the
 * figure the reading had, the „gelesen“ beside it).
 *
 * Every call is wrapped: a browser that refuses storage (private mode, blocked
 * site data, a full quota) gets a working screen, and only a reload forgets.
 */
import type { Draft, DraftVertex, DraftWall } from "./draft";

const PREFIX = "k60.umriss";

/** Bumped when the shape changes; an entry of another version is none. */
const VERSION = 1;

/** Where a drawing on `/umriss` came from — the four ways in. */
export type DraftContext =
  | { way: "neu" }
  | { way: "vorgang"; caseId: string }
  | { way: "lesung" | "form"; caseId: string; basis: string };

export interface StoredDraft {
  /** ISO timestamp of the last change that was written. */
  savedAt: string;
  draft: Draft;
}

export function draftStorageKey(context: DraftContext): string {
  return context.way === "neu" ? `${PREFIX}.neu` : `${PREFIX}.${context.way}.${context.caseId}`;
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Asking for it can throw already (blocked site data).
    return null;
  }
}

/**
 * Whether a drawing kept here would survive a reload — what the screen's foot
 * says. A write and its removal, both wrapped: a storage that answers reads
 * but refuses writes (a full quota, an old private mode) keeps nothing.
 */
export function draftStorageWorks(): boolean {
  const store = storage();
  if (store === null) return false;
  const probe = `${PREFIX}.probe`;
  try {
    store.setItem(probe, "1");
    store.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function basisOf(context: DraftContext): string | null {
  return context.way === "lesung" || context.way === "form" ? context.basis : null;
}

/** The wall as it is written: what the draft holds, minus anything a screen hung on it in passing. */
function wallOut(wall: DraftWall): DraftWall {
  return {
    lengthMm: wall.lengthMm,
    label: wall.label,
    ...(wall.readMm != null ? { readMm: wall.readMm } : {}),
    ...(wall.fromWallId != null ? { fromWallId: wall.fromWallId } : {}),
  };
}

/** Written on every change; `false` where the browser refused it. */
export function saveStoredDraft(context: DraftContext, draft: Draft, now: Date = new Date()): boolean {
  const store = storage();
  if (store === null) return false;
  const basis = basisOf(context);
  const entry = {
    v: VERSION,
    savedAt: now.toISOString(),
    ...(basis !== null ? { basis } : {}),
    draft: {
      closed: draft.closed,
      vertices: draft.vertices.map((vertex) => ({ xMm: vertex.xMm, yMm: vertex.yMm })),
      walls: draft.walls.map(wallOut),
    },
  };
  try {
    store.setItem(draftStorageKey(context), JSON.stringify(entry));
    return true;
  } catch {
    return false;
  }
}

export function clearStoredDraft(context: DraftContext): void {
  try {
    storage()?.removeItem(draftStorageKey(context));
  } catch {
    // Refused: what is left is offered once more and can be thrown away then.
  }
}

function isPositiveWhole(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function vertexIn(value: unknown): DraftVertex | null {
  if (typeof value !== "object" || value === null) return null;
  const { xMm, yMm } = value as Record<string, unknown>;
  if (typeof xMm !== "number" || typeof yMm !== "number") return null;
  if (!Number.isFinite(xMm) || !Number.isFinite(yMm)) return null;
  return { xMm, yMm };
}

function wallIn(value: unknown): DraftWall | null {
  if (typeof value !== "object" || value === null) return null;
  const { lengthMm, label, readMm, fromWallId } = value as Record<string, unknown>;
  if (lengthMm !== null && !isPositiveWhole(lengthMm)) return null;
  if (label !== null && typeof label !== "string") return null;
  if (readMm !== undefined && readMm !== null && !isPositiveWhole(readMm)) return null;
  if (fromWallId !== undefined && fromWallId !== null && typeof fromWallId !== "string") return null;
  return {
    lengthMm,
    label,
    ...(isPositiveWhole(readMm) ? { readMm } : {}),
    ...(typeof fromWallId === "string" ? { fromWallId } : {}),
  };
}

/**
 * A drawing the board can show: one wall per step, every wall waagerecht or
 * senkrecht, a closed ring back at its start. Anything else is not one of
 * ours and is read as nothing.
 */
function draftIn(value: unknown): Draft | null {
  if (typeof value !== "object" || value === null) return null;
  const { closed, vertices, walls } = value as Record<string, unknown>;
  if (typeof closed !== "boolean" || !Array.isArray(vertices) || !Array.isArray(walls)) return null;
  if (walls.length === 0 || vertices.length !== walls.length + 1) return null;

  const points = vertices.map(vertexIn);
  const sides = walls.map(wallIn);
  if (points.some((point) => point === null) || sides.some((side) => side === null)) return null;
  const corners = points as DraftVertex[];
  for (let at = 1; at < corners.length; at += 1) {
    const from = corners[at - 1]!;
    const to = corners[at]!;
    if (from.xMm !== to.xMm && from.yMm !== to.yMm) return null;
  }
  const first = corners[0]!;
  const last = corners.at(-1)!;
  if (closed && (first.xMm !== last.xMm || first.yMm !== last.yMm)) return null;
  return { closed, vertices: corners, walls: sides as DraftWall[] };
}

/**
 * The drawing kept for this way in, or `null`: none kept, not readable, of
 * another version, or taken from another Stand or reading than the one the
 * screen now starts from.
 */
export function loadStoredDraft(context: DraftContext): StoredDraft | null {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(draftStorageKey(context)) ?? null;
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const entry = parsed as Record<string, unknown>;
  if (entry.v !== VERSION) return null;
  if (typeof entry.savedAt !== "string" || Number.isNaN(Date.parse(entry.savedAt))) return null;
  if ((entry.basis ?? null) !== basisOf(context)) return null;
  const draft = draftIn(entry.draft);
  return draft === null ? null : { savedAt: entry.savedAt, draft };
}
